import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { useState } from "react";
import styled from "styled-components";
import {
  BenefitServiceLine,
  BenefitTierPreference,
} from "types/BenefitSelection/BenefitSelection";

import { PAYER_CLASS_OPTIONS, serviceLineLabel, tierLabel } from "./constants";
import { emptyException, ExceptionDialog } from "./ExceptionDialog";
import { IBenefitExceptionForm, reorder } from "./mapper";
import { SectionTitle } from "./Surface";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
`;

interface IExceptionsListProps {
  exceptions: IBenefitExceptionForm[];
  accountServiceLine: BenefitServiceLine | null;
  /** What a row with no tier of its own reads, in the table and in the dialog. */
  accountTierPreference: BenefitTierPreference;
  payerOptions: Array<{ label: string; value: number }>;
  loadingPayers: boolean;
  onChange: (exceptions: IBenefitExceptionForm[]) => void;
}

/**
 * CLE-2792 — the ordered exception list. First match wins, and the order is the rule.
 *
 * ⚠️ THAT RULE IS NOT WRITTEN ANYWHERE ON SCREEN (review round 3), because the reorder carets state
 * it: a list you can move a row up and down in is a list that is read in order. The sentence above
 * the list answers the question somebody actually arrives with — when do I need one of these.
 *
 * 🚨 THE LAST ROW IS NOT A ROW. It restates the service line chosen above so the list reads top to
 * bottom with no hidden default, and it cannot be dragged or removed. With no exceptions at all
 * there is nothing for it to be the exception TO, so it is hidden rather than left standing alone
 * restating a choice the picker above has already made.
 */
export function ExceptionsList({
  exceptions,
  accountServiceLine,
  accountTierPreference,
  payerOptions,
  loadingPayers,
  onChange,
}: IExceptionsListProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const payerLabel = (payerIds: number[]): string =>
    payerIds
      .map(
        (id) =>
          payerOptions.find((option) => option.value === id)?.label ?? `#${id}`
      )
      .sort()
      .join(", ");

  const describeScope = (exception: IBenefitExceptionForm): string => {
    if (exception.payerClass) {
      const label =
        PAYER_CLASS_OPTIONS.find(
          (option) => option.value === exception.payerClass
        )?.name ?? exception.payerClass;
      return `${label} payer class`;
    }
    const parts: string[] = [];
    if (exception.payerIds.length) parts.push(payerLabel(exception.payerIds));
    if (exception.planKeywords.length) {
      parts.push(`plans containing ${exception.planKeywords.join(", ")}`);
    }
    return parts.join(" · ") || "No scope";
  };

  const openAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const openEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSave = (exception: IBenefitExceptionForm) => {
    if (editingIndex === null) {
      onChange([...exceptions, exception]);
    } else {
      onChange(
        exceptions.map((current, index) =>
          index === editingIndex ? exception : current
        )
      );
    }
    setDialogOpen(false);
  };

  const move = (from: number, to: number) =>
    onChange(reorder(exceptions, from, to));

  const confirmRemove = (index: number) => {
    const exception = exceptions[index];
    confirmDialog({
      header: "Remove this exception?",
      style: { width: "min(520px, 95vw)" },
      message: `${describeScope(exception)} → ${serviceLineLabel(
        exception.source
      )} stops applying from the next save. Those checks will read ${serviceLineLabel(
        accountServiceLine
      )}, the account's service line.`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Remove exception",
      rejectLabel: "Keep it",
      accept: () => onChange(exceptions.filter((_, i) => i !== index)),
    });
  };

  // Extracted rather than inline in the JSX so `react/no-unstable-nested-components` does not treat
  // them as components redefined on every render — the convention the payer-rules tables use.
  const orderCellBody = (_row: IBenefitExceptionForm, { rowIndex }: any) => (
    <div className="flex align-items-center gap-1">
      <Button
        type="button"
        icon="pi pi-chevron-up"
        className="p-button-text p-button-sm"
        aria-label={`Move exception ${rowIndex + 1} up`}
        disabled={rowIndex === 0}
        data-testid={`benefitSelection.exception.${rowIndex}.up`}
        onClick={() => move(rowIndex, rowIndex - 1)}
      />
      <Button
        type="button"
        icon="pi pi-chevron-down"
        className="p-button-text p-button-sm"
        aria-label={`Move exception ${rowIndex + 1} down`}
        disabled={rowIndex === exceptions.length - 1}
        data-testid={`benefitSelection.exception.${rowIndex}.down`}
        onClick={() => move(rowIndex, rowIndex + 1)}
      />
    </div>
  );

  const scopeCellBody = (_row: IBenefitExceptionForm, { rowIndex }: any) => (
    <span data-testid={`benefitSelection.exception.${rowIndex}.scope`}>
      {describeScope(exceptions[rowIndex])}
    </span>
  );

  const sourceCellBody = (_row: IBenefitExceptionForm, { rowIndex }: any) => (
    <span data-testid={`benefitSelection.exception.${rowIndex}.source`}>
      {serviceLineLabel(exceptions[rowIndex].source)}
    </span>
  );

  // A row that names no tier reads the account's, which is what the PUT sends for it — so the cell
  // prints that rather than a dash, which would read as "no tier applies here".
  const tierCellBody = (_row: IBenefitExceptionForm, { rowIndex }: any) => (
    <span data-testid={`benefitSelection.exception.${rowIndex}.tier`}>
      {tierLabel(exceptions[rowIndex].tierPreference ?? accountTierPreference)}
    </span>
  );

  const provenanceCellBody = (_row: IBenefitExceptionForm, { rowIndex }: any) =>
    exceptions[rowIndex].key.startsWith("new-") ? (
      <Tag value="Unsaved" severity="warning" />
    ) : null;

  const actionsCellBody = (_row: IBenefitExceptionForm, { rowIndex }: any) => (
    <div className="flex gap-1">
      <Button
        type="button"
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        aria-label="Edit exception"
        data-testid={`benefitSelection.exception.${rowIndex}.edit`}
        onClick={() => openEdit(rowIndex)}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        aria-label="Remove exception"
        data-testid={`benefitSelection.exception.${rowIndex}.remove`}
        onClick={() => confirmRemove(rowIndex)}
      />
    </div>
  );

  return (
    <div
      className="flex flex-column gap-3 p-3 border-round border-1 border-gray-200"
      data-testid="benefitSelection.exceptions"
    >
      <SectionTitle>Exceptions</SectionTitle>

      <span className="text-sm text-color-secondary">
        Use an exception when certain payers or plans should read a different
        service line than the one chosen above.
      </span>

      {exceptions.length === 0 ? (
        <span
          className="text-sm text-color-secondary"
          data-testid="benefitSelection.exceptions.empty"
        >
          No exceptions.
        </span>
      ) : (
        <StyledDataTable
          value={exceptions}
          dataKey="key"
          reorderableRows
          onRowReorder={(event: any) => onChange(event.value)}
          data-testid="benefitSelection.exceptionsTable"
          className="w-full"
        >
          <Column header="" style={{ width: "9rem" }} body={orderCellBody} />
          <Column header="Applies to" body={scopeCellBody} />
          <Column
            header="Copay & co-insurance read from"
            body={sourceCellBody}
          />
          <Column
            header="Tier"
            style={{ width: "10rem" }}
            body={tierCellBody}
          />
          <Column
            header=""
            style={{ width: "6rem" }}
            body={provenanceCellBody}
          />
          <Column header="" style={{ width: "7rem" }} body={actionsCellBody} />
        </StyledDataTable>
      )}

      {exceptions.length > 0 && (
        <div
          className="flex align-items-center gap-3 p-2 border-round surface-50 text-sm"
          data-testid="benefitSelection.exceptions.catchAll"
        >
          <span>
            Everyone else reads the {serviceLineLabel(accountServiceLine)}{" "}
            service line.
          </span>
        </div>
      )}

      <div className="flex justify-content-center">
        <Button
          type="button"
          icon="pi pi-plus"
          label="Add exception"
          className="p-button-outlined p-button-sm"
          disabled={!accountServiceLine}
          data-testid="benefitSelection.addException"
          onClick={openAdd}
        />
      </div>

      {accountServiceLine && (
        <ExceptionDialog
          visible={dialogOpen}
          isNew={editingIndex === null}
          value={
            editingIndex === null ? emptyException() : exceptions[editingIndex]
          }
          accountTierPreference={accountTierPreference}
          payerOptions={payerOptions}
          loadingPayers={loadingPayers}
          onSave={handleSave}
          onHide={() => setDialogOpen(false)}
        />
      )}
    </div>
  );
}
